import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { getFirm, getMyMember, listMembers, listProjects, listTeams } from '../lib/api';
import type { Firm, Member, Project, Team } from '../lib/types';

interface AuthState {
  loading: boolean;
  session: Session | null;
  me: Member | null;
  firm: Firm | null;
  members: Member[];
  teams: Team[];
  projects: Project[];
  isManager: boolean;
  reload: () => Promise<void>;
  signOut: () => Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<Session | null>(null);
  const [me, setMe] = useState<Member | null>(null);
  const [firm, setFirm] = useState<Firm | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);

  const load = useCallback(async (s: Session | null) => {
    setSession(s);
    if (!s) {
      setMe(null); setFirm(null); setMembers([]); setTeams([]); setProjects([]);
      setLoading(false);
      return;
    }
    try {
      const m = await getMyMember(s.user.id);
      setMe(m);
      if (m) {
        const [f, ms, ts, ps] = await Promise.all([getFirm(), listMembers(), listTeams(), listProjects()]);
        setFirm(f); setMembers(ms); setTeams(ts); setProjects(ps);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!supabase) { setLoading(false); return; }
    supabase.auth.getSession().then(({ data }) => load(data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      // Defer so we never call Supabase inside its own auth callback.
      setTimeout(() => load(s), 0);
    });
    return () => sub.subscription.unsubscribe();
  }, [load]);

  const value = useMemo<AuthState>(() => ({
    loading, session, me, firm, members, teams, projects,
    isManager: me?.role === 'owner' || me?.role === 'manager',
    reload: () => load(session),
    signOut: async () => { await supabase?.auth.signOut(); },
  }), [loading, session, me, firm, members, teams, projects, load]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth must be used inside AuthProvider');
  return v;
}
